import React, { useState } from "react";
import { X, MessageCircle, Clock, Copy, CheckCircle, Shield, AlertCircle } from "lucide-react";
import { toast } from "react-toastify";

const SMSMessagesPopup = ({ smsMessages, onClose }) => {
  const [isClosing, setIsClosing] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [showVerificationOverlay, setShowVerificationOverlay] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 400);
  };

  const handleCopyCode = async (code, index) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedIndex(index);
      toast.success("تم نسخ الكود بنجاح!");
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (error) {
      toast.error("فشل في نسخ الكود");
    }
  };

  const handleCopyMessage = async (text, index) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIndex(`msg-${index}`);
      toast.success("تم نسخ الرسالة بنجاح!");
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (error) {
      toast.error("فشل في نسخ الرسالة");
    }
  };

  const handleVerifyMessage = (message, index) => {
    setSelectedMessage({ ...message, index });
    setShowVerificationOverlay(true);
  };

  const handleCloseVerificationOverlay = () => {
    setShowVerificationOverlay(false);
    setSelectedMessage(null);
  };

  const handleConfirmVerification = () => {
    if (selectedMessage?.code) {
      navigator.clipboard.writeText(selectedMessage.code);
      toast.success("تم التحقق من الكود ونسخه بنجاح!");
    } else {
      toast.success("تم التحقق من الرسالة بنجاح!");
    }
    handleCloseVerificationOverlay();
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleString('ar-EG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[5000] p-4">
        <div
          className={`${
            isClosing ? "animate-close" : "animate-popup"
          } bg-slate-900 rounded-2xl w-full max-w-2xl border border-slate-800 shadow-xl animate-in zoom-in-95 z-[5100] max-h-[90vh] overflow-hidden`}
        >
          <div className="flex flex-col h-full">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-slate-800">
              <div className="flex items-center gap-3">
                <MessageCircle className="w-6 h-6 text-blue-400" />
                <h3 className="text-xl font-bold text-white">رسائل SMS المستلمة</h3>
              </div>
              <button
                onClick={handleClose}
                className="p-2 hover:bg-red-800/30 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>

            {/* SMS Status */}
            <div className="p-6 border-b border-slate-800">
              <div className="bg-slate-800/50 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-slate-400 font-medium">حالة الرقم:</span>
                  <span className={`font-bold text-lg px-3 py-1 rounded-lg ${
                    smsMessages.sim_status === 'CANCELED' ? 'text-red-400 bg-red-500/20' :
                    smsMessages.sim_status === 'COMPLETED' ? 'text-green-400 bg-green-500/20' :
                    'text-yellow-400 bg-yellow-500/20'
                  }`}>
                    {smsMessages.sim_status}
                  </span>
                </div>
                
                {smsMessages.phone && (
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-slate-400">رقم الهاتف:</span>
                    <span className="text-white font-mono text-lg">{smsMessages.phone}</span>
                  </div>
                )}

                {smsMessages.expires && (
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">انتهاء الصلاحية:</span>
                    <span className="text-white">{formatDate(smsMessages.expires)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Messages Content */}
            <div className="flex-1 overflow-hidden">
              {smsMessages.sms && smsMessages.sms.length > 0 ? (
                <div className="h-full overflow-y-auto p-6">
                  <div className="space-y-4">
                    {smsMessages.sms.map((message, index) => (
                      <div 
                        key={index} 
                        className="bg-slate-800/50 rounded-xl p-4 border-r-4 border-blue-500 hover:bg-slate-800/70 transition-colors"
                      >
                        {/* Message Header */}
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <span className="text-blue-400 font-bold text-lg">{message.sender}</span>
                            <span className="text-xs bg-blue-500/20 text-blue-300 px-2 py-1 rounded-full">
                              المرسل
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            
                            <button
                              onClick={() => handleVerifyMessage(message, index)}
                              className="bg-purple-600 hover:bg-purple-500 text-white px-3 py-1 rounded-lg text-xs flex items-center gap-1 transition-colors"
                              title="التحقق من الرسالة"
                            >
                              <Shield className="w-3 h-3" />
                              تحقق
                            </button>
                          </div>
                        </div>

                        {/* Message Text */}
                        <div className="mb-4">
                          <div className="bg-slate-700/50 rounded-lg p-3 relative group">
                            <p className="text-white leading-relaxed">{message.text}</p>
                            <button
                              onClick={() => handleCopyMessage(message.text, index)}
                              className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-slate-600 rounded"
                              title="نسخ الرسالة"
                            >
                              {copiedIndex === `msg-${index}` ? (
                                <CheckCircle className="w-4 h-4 text-green-400" />
                              ) : (
                                <Copy className="w-4 h-4 text-slate-400" />
                              )}
                            </button>
                          </div>
                        </div>

                        {/* Verification Code */}
                        {message.code && (
                          <div className="flex items-center justify-between bg-emerald-500/20 border border-emerald-500/30 rounded-lg p-3">
                            <div className="flex items-center gap-2">
                              <span className="text-emerald-400 font-medium">كود التحقق:</span>
                              <span className="text-emerald-300 font-mono text-xl font-bold">
                                {message.code}
                              </span>
                            </div>
                            <button
                              onClick={() => handleCopyCode(message.code, index)}
                              className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white px-3 py-1.5 rounded-lg transition-colors"
                              title="نسخ الكود"
                            >
                              {copiedIndex === index ? (
                                <>
                                  <CheckCircle className="w-4 h-4" />
                                  <span className="text-sm">تم النسخ</span>
                                </>
                              ) : (
                                <>
                                  <Copy className="w-4 h-4" />
                                  <span className="text-sm">نسخ</span>
                                </>
                              )}
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-slate-400 p-6">
                  <MessageCircle className="w-16 h-16 mb-4 opacity-50" />
                  <p className="text-lg font-medium mb-2">لا توجد رسائل مستلمة</p>
                  <p className="text-sm text-center">
                    لم يتم استلام أي رسائل SMS على هذا الرقم حتى الآن
                  </p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-slate-800">
              <div className="flex items-center justify-between">
                <div className="text-sm text-slate-400">
                  {smsMessages.sms ? (
                    <span>إجمالي الرسائل: {smsMessages.sms.length}</span>
                  ) : (
                    <span>لا توجد رسائل</span>
                  )}
                </div>
                <button
                  onClick={handleClose}
                  className="px-6 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                >
                  إغلاق
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Verification Overlay */}
      {showVerificationOverlay && selectedMessage && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[5200] p-4">
          <div className="bg-slate-800 rounded-2xl w-full max-w-md border border-slate-700 shadow-2xl animate-in zoom-in-95">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-purple-500/20 p-2 rounded-lg">
                  <Shield className="w-6 h-6 text-purple-400" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">التحقق من الرسالة</h3>
                  <p className="text-sm text-slate-400">تأكيد تفاصيل الرسالة المستلمة</p>
                </div>
              </div>

              <div className="space-y-4 mb-6">
                <div className="bg-slate-700/50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-slate-400 text-sm">المرسل:</span>
                    <span className="text-white font-medium">{selectedMessage.sender}</span>
                  </div>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-slate-400 text-sm">التاريخ:</span>
                    <span className="text-white text-sm">{formatDate(selectedMessage.date)}</span>
                  </div>
                  <div className="mb-3">
                    <span className="text-slate-400 text-sm block mb-2">نص الرسالة:</span>
                    <div className="bg-slate-600/50 rounded p-3">
                      <p className="text-white text-sm leading-relaxed">{selectedMessage.text}</p>
                    </div>
                  </div>
                  {selectedMessage.code && (
                    <div className="bg-emerald-500/20 border border-emerald-500/30 rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <span className="text-emerald-400 font-medium">كود التحقق:</span>
                        <span className="text-emerald-300 font-mono text-lg font-bold">
                          {selectedMessage.code}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-amber-400 text-sm font-medium mb-1">تنبيه هام</p>
                    <p className="text-amber-300 text-xs leading-relaxed">
                      تأكد من صحة المرسل وصحة الكود قبل استخدامه. لا تشارك كود التحقق مع أي شخص آخر.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleCloseVerificationOverlay}
                  className="flex-1 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                >
                  إلغاء
                </button>
                <button
                  onClick={handleConfirmVerification}
                  className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  <Shield className="w-4 h-4" />
                  تأكيد التحقق
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SMSMessagesPopup;